import { useState } from "react";
import { Box, Grid, Card, CardActionArea, CardMedia, Typography, Button, TextField } from "@mui/material";

const normalizeStatus = (status) => {
  if (status === 1 || status === "1" || status === true || status === "true") return 1;
  return 0;
};

const extractByStatus = (nodes = [], targetStatus = 0) => {
  if (!Array.isArray(nodes)) return [];

  return nodes.reduce((acc, node) => {
    const children = Array.isArray(node?.children_recursive) ? node.children_recursive : [];
    const matchedChildren = extractByStatus(children, targetStatus);
    const selfMatched = normalizeStatus(node?.status) === targetStatus;

    if (selfMatched) {
      // If the parent is inactive, keep full children tree under it.
      acc.push({ ...node, children_recursive: children });
      return acc;
    }

    if (matchedChildren.length) {
      acc.push(...matchedChildren);
    }

    return acc;
  }, []);
};

const InactiveCategoriesTab = ({
  categories,
  getName,
  getDesc,
  isActiveStatus,
  getImageUrl,
  formatDate,
  getChildrenCount,
  openAddDialog,
  openEditDialog,
  onToggleStatus,
  statusLoadingId,
  isAr,
  sortOrder,
  onToggleSortOrder,
}) => {
  const [searchText, setSearchText] = useState("");

  const inactiveTree = extractByStatus(categories, 0);
  const matchesSearch = (cat, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [cat?.name_ar, cat?.name_en, cat?.desc_ar, cat?.desc_en]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const filterTreeBySearch = (nodes, query) => {
    if (!Array.isArray(nodes)) return [];

    return nodes.reduce((acc, node) => {
      const children = Array.isArray(node?.children_recursive) ? node.children_recursive : [];
      const matchedChildren = filterTreeBySearch(children, query);
      const selfMatched = matchesSearch(node, query);

      if (selfMatched || matchedChildren.length) {
        acc.push({ ...node, children_recursive: matchedChildren });
      }

      return acc;
    }, []);
  };

  const normalizedSearch = searchText.trim();
  const visibleInactiveTree = normalizedSearch ? filterTreeBySearch(inactiveTree, normalizedSearch) : inactiveTree;

  const renderChildren = (children, level = 1) => {
    if (!children || !children.length) return null;

    return (
      <Box sx={{ mt: 1, pl: `${level * 2}px` }}>
        {children.map((child) => (
          <Box key={child.id} sx={{ display: "flex", mb: 1 }}>
            <Box sx={{ width: 56, height: 56, mr: 1, flexShrink: 0 }}>
              <img
                src={getImageUrl(child.image)}
                alt={getName(child)}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, background: "#f5f5f5" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {getName(child)}
                  </Typography>
                  {getDesc(child) ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {getDesc(child)}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {formatDate(child.created_at)} {` • `} {`الفئات الفرعية: ${getChildrenCount(child)}`}
                  </Typography>
                </Box>
                <Box>
                  <Button size="small" variant="outlined" onClick={(e) => openAddDialog(child.id, e)}>
                    إضافة فرعية
                  </Button>
                  <Button size="small" variant="text" onClick={(e) => openEditDialog(child, e)}>
                    تعديل
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color={isActiveStatus(child.status) ? "error" : "success"}
                    disabled={statusLoadingId === child.id}
                    onClick={(e) => onToggleStatus(child, e)}
                  >
                    {isActiveStatus(child.status) ? "إلغاء التفعيل" : "تفعيل"}
                  </Button>
                </Box>
              </Box>
              {renderChildren(child.children_recursive, level + 1)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (!inactiveTree.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">لا توجد فئات غير نشطة</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          label="بحث في الفئات غير النشطة"
          placeholder="ابحث بالاسم أو الوصف"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button variant="outlined" onClick={onToggleSortOrder} sx={{ whiteSpace: "nowrap" }}>
          {sortOrder === "desc" ? "الأحدث -> الأقدم" : "الأقدم -> الأحدث"}
        </Button>
      </Box>

      {!visibleInactiveTree.length ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">لا توجد نتائج مطابقة</Typography>
        </Box>
      ) : null}

      <Grid container spacing={3}>
        {visibleInactiveTree.map((cat) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={cat.id}>
          <Card sx={{ height: "100%", transition: "transform .18s", "&:hover": { transform: "translateY(-6px)", boxShadow: 4 } }}>
            <CardActionArea sx={{ display: "block", textAlign: isAr ? "right" : "left" }}>
              <CardMedia component="img" image={getImageUrl(cat.image)} alt={getName(cat)} sx={{ height: 160, objectFit: "cover", backgroundColor: "#f5f5f5" }} />
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {getName(cat)}
                    </Typography>
                    {getDesc(cat) ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {getDesc(cat)}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {formatDate(cat.created_at)} {` • `} {`الفئات الفرعية: ${getChildrenCount(cat)}`}
                    </Typography>
                  </Box>
                  <Box>
                    <Button size="small" variant="outlined" onClick={(e) => openAddDialog(cat.id, e)} sx={{ mr: 1 }}>
                      إضافة فرعية
                    </Button>
                    <Button size="small" variant="text" onClick={(e) => openEditDialog(cat, e)}>
                      تعديل
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color={isActiveStatus(cat.status) ? "error" : "success"}
                      disabled={statusLoadingId === cat.id}
                      onClick={(e) => onToggleStatus(cat, e)}
                    >
                      {isActiveStatus(cat.status) ? "إلغاء التفعيل" : "تفعيل"}
                    </Button>
                  </Box>
                </Box>

                {renderChildren(cat.children_recursive)}
              </Box>
            </CardActionArea>
          </Card>
        </Grid>
        ))}
      </Grid>
    </>
  );
};

export default InactiveCategoriesTab;